import { Button } from "@headlessui/react"
import { Link } from "@tanstack/react-router"
import type { ToPathOption } from "@tanstack/react-router"
import React from "react"

import type { router } from "@/main"

enum ButtonType {
  Danger = "danger",
  Primary = "primary",
  Reset = "reset",
  Blue = "blue",
}

type RouterType = typeof router

// Utility type to infer dynamic route parameters
// Record<string, never> rather than `{}` for the no-params branch: `{}` means
// "any non-nullish value" in TypeScript, so it would accept a string or a
// number for `params`, which is the opposite of the intent here.
type DynamicParams<T extends string> =
  T extends `${infer _Start}:${infer Param}`
    ? Record<Param, string>
    : Record<string, never>

type BaseProps = {
  children: React.ReactNode
  className?: string
  color?: string
}

type LinkProps<T extends string> = BaseProps & {
  params?: DynamicParams<T>
  search?: (prev: any) => any
  to: ToPathOption<RouterType, T>
  type?: never
  disabled?: never
  href?: never
  onClick?: never
}

type HrefProps = BaseProps & {
  href: string // Use the `href` prop for external links
  target?: "_blank" | "_self" | "_parent" | "_top"
  disabled?: never
  download?: boolean
  onClick?: never
  params?: never
  search?: never
  to?: never
  type?: never
}

type ButtonProps = BaseProps & {
  disabled?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  type?: "button" | "submit" | "reset"
  href?: never
  params?: never
  search?: never
  to?: never
}

type CombinedButtonProps<T extends string> =
  | LinkProps<T>
  | HrefProps
  | ButtonProps

const SwiftButton = <T extends string>({
  children,
  className,
  color,
  disabled = false,
  href,
  onClick,
  params,
  search,
  to,
  type = "button",
  ...restProps
}: CombinedButtonProps<T>) => {
  const baseStyles =
    "flex items-center justify-center text-white font-medium focus-visible:ring-4 focus:outline-none rounded-lg text-sm px-4 py-2 disabled:pointer-events-none disabled:opacity-50 w-full"

  const getColorStyles = (buttonColor: string | undefined) => {
    switch (buttonColor) {
      case ButtonType.Danger:
        return "bg-red-500 text-white"
      case ButtonType.Reset:
        return "bg-gray-300 hover:bg-gray-400 focus-visible:ring-gray-300"
      case ButtonType.Primary:
        return "bg-gray-700 hover:bg-gray-800 focus-visible:ring-gray-300"
      case ButtonType.Blue:
        return "bg-blue-700 hover:bg-blue-800 focus-visible:ring-blue-300"
      default:
        return "bg-gray-500 text-white"
    }
  }

  const buttonClasses = `${baseStyles} ${getColorStyles(color)}`

  if (to) {
    return (
      // @ts-ignore
      <Link
        className={buttonClasses}
        params={params}
        search={search}
        to={to}
        {...restProps}
      >
        {children}
      </Link>
    )
  }

  if (href) {
    return (
      <a href={href} className={`${buttonClasses} ${className}`} {...restProps}>
        {children}
      </a>
    )
  }

  return (
    <Button
      className={`${buttonClasses} ${className}`}
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      type={type}
      {...restProps}
    >
      {children}
    </Button>
  )
}

export default SwiftButton
